import { createSlice } from '@reduxjs/toolkit';

import { StatusTransport } from '@pages/hubs/Garage/enum/statusTransport.enums';
import {
  acceptedTransportWS,
  addTransportWS,
  getTransportsV2,
  updateTransportWS,
} from '@pages/hubs/Garage/store/garage-thunk';
import { isListOver } from '@shared/functions/isListOver';
import { changeCoverPhoto } from '@shared/utils/hubs/changeCoverPhoto';

import type {
  FilterParams,
  GetTransportsParams,
  RecognitionDataType,
  RootGarageItemsData,
  RootMeta,
  VehicleRegistrationRecognitionData,
  Vehicles,
} from './types';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { AssignPeoplePermissionsEnum } from '@shared/enums/assignPeoplePermissions.enum';
import type { TransportTypeEnum } from '@shared/enums/garage.enums';
import type { AssignPeopleSelectValueModel } from '@shared/models/assignPeopleSelectValue.model';
import type { VinAndLicensePlateInfoType } from '@shared/models/garage/vin-info';
import type { MediaType } from '@shared/models/media.model';
import type { Nullable } from '@shared/types/base-custom.type';

const initialState = {
  transportType: '' as TransportTypeEnum,
  insuranceRecognitionData: {} as RecognitionDataType,
  insuranceVehicle: {} as Vehicles,
  vinAndLicensePlate: {} as VinAndLicensePlateInfoType,
  insuranceScanAiDocument: null as MediaType | null,
  isLoadingGarage: false,
  isEmptyGarageWithFilter: false,
  isListOver: false as boolean | number,
  transports: {
    data: [] as RootGarageItemsData[],
    meta: {} as RootMeta,
  },
  vehicleRegistrationScanData: {
    vehicleRegistrationRecognitionData: null as Nullable<VehicleRegistrationRecognitionData>,
    vehicleRegistrationScanDocument: null as Nullable<MediaType>,
  },
  share: {
    sharePeopleList: [] as AssignPeopleSelectValueModel[],
    selectShareUser: null as AssignPeopleSelectValueModel | null,
  },
  currentTransport: {} as RootGarageItemsData,
  filterParams: {
    query: '',
    shared_filter: [],
  } as FilterParams,
  isEditMode: false,
};
type InitialStateType = typeof initialState;
export const garageSlice = createSlice({
  name: 'garageV2',
  initialState: initialState as InitialStateType,
  reducers: {
    // ---------- garage common --------------
    setEditMode(state, action: PayloadAction<{ editMode: boolean }>) {
      state.isEditMode = action.payload.editMode;
    },
    setLoadingGarage(state, action: PayloadAction<{ isLoading: boolean }>) {
      state.isLoadingGarage = action.payload.isLoading;
    },
    setGarageTransportType(state, action: PayloadAction<{ transportType: TransportTypeEnum }>) {
      state.transportType = action.payload.transportType;
    },

    setCurrentTransport(state, action: PayloadAction<{ transport: RootGarageItemsData }>) {
      state.currentTransport = action.payload.transport;
    },
    clearCurrentTransport(state) {
      state.currentTransport = initialState.currentTransport;
    },

    deleteCurrentInsurance(state, action: PayloadAction<{ insuranceID: number }>) {
      const index = state.currentTransport.insurances.findIndex(
        (insurance) => insurance.id === action.payload.insuranceID,
      );
      if (index !== -1) state.currentTransport.insurances.splice(index, 1);
    },
    deleteCurrentLicence(state, action: PayloadAction<{ licenceID: number }>) {
      const index = state.currentTransport.licenses.findIndex(
        (license) => license.id === action.payload.licenceID,
      );
      if (index !== -1) state.currentTransport.licenses.splice(index, 1);
    },
    deleteCurrentSticker(state, action: PayloadAction<{ stickerID: number }>) {
      const index = state.currentTransport.stickers.findIndex(
        (stickers) => stickers.id === action.payload.stickerID,
      );
      if (index !== -1) state.currentTransport.stickers.splice(index, 1);
    },

    setInsuranceVehicle(state, action: PayloadAction<{ chooseVehicle: Vehicles }>) {
      state.insuranceVehicle = action.payload.chooseVehicle;
    },

    // -------------------- main-page --------------------
    setFilterParams(state, action: PayloadAction<{ params: FilterParams }>) {
      state.filterParams = action.payload.params;
    },
    clearGarageFilters(state) {
      state.filterParams = { ...state.filterParams, query: '' };
    },
    isEmptyDataGarageWithFilter(
      state,
      action: PayloadAction<{
        filters: Pick<GetTransportsParams, 'shared_filter' | 'query'>;
        garageData: RootGarageItemsData[];
      }>,
    ) {
      const { shared_filter, query } = action.payload.filters;
      state.isEmptyGarageWithFilter =
        (!!shared_filter?.length || !!query) && !action.payload.garageData.length;
    },

    deleteCurrentTransport(state, action: PayloadAction<{ transportID: number }>) {
      const index = state.transports.data.findIndex(
        (transport) => transport.id === action.payload.transportID,
      );
      if (index !== -1) state.transports.data.splice(index, 1);

      if (
        !state.transports.data.length &&
        (!!state.filterParams.shared_filter.length || !!state.filterParams.query)
      ) {
        state.isEmptyGarageWithFilter = true;
      }
    },
    setTransportDataWithNewPosition(
      state,
      action: PayloadAction<{ newPositionData: RootGarageItemsData[] }>,
    ) {
      state.transports.data = action.payload.newPositionData;
    },
    clearTransportsState(state) {
      state.transports = initialState.transports;
    },
    // ---------- garage files --------------
    addDocuments(state, action: PayloadAction<{ newDocuments: MediaType[] }>) {
      state.currentTransport.documents = action.payload.newDocuments;
    },

    // ---------- garage gallery --------------
    addPhoto(state, action: PayloadAction<{ newMedia: MediaType[] }>) {
      state.currentTransport.photos = action.payload.newMedia;
    },
    updateCoverPhoto(state, action: PayloadAction<{ coverPhoto: MediaType }>) {
      state.currentTransport.cover_photo = action.payload.coverPhoto;
    },
    removeCoverPhoto(state, action: PayloadAction<{ deleteCoverID: number; isCover: boolean | undefined }>) {
      const deleteCoverIndex = state.currentTransport.photos.findIndex(
        (photo) => photo.id === action.payload.deleteCoverID,
      );
      if (deleteCoverIndex !== -1) state.currentTransport.photos.splice(deleteCoverIndex, 1);
      if (!!state.currentTransport.photos.length) {
        const { photos } = changeCoverPhoto(state.currentTransport.photos);

        const hasCoverPhoto = state.currentTransport.photos.filter((photo) => photo.is_cover);

        state.currentTransport.photos = [...photos];
        if (!hasCoverPhoto.length) {
          state.currentTransport.cover_photo = photos.length ? photos[photos.length - 1] : null;
        }
      } else state.currentTransport.cover_photo = null;
    },
    // ---------- garage insurance scan ai --------------
    setVinAndLicensePlateData: (state, action: PayloadAction<VinAndLicensePlateInfoType>) => {
      state.vinAndLicensePlate = action.payload;
    },
    setInsuranceRecognitionData(state, action: PayloadAction<{ recognitionData: RecognitionDataType }>) {
      state.insuranceRecognitionData = action.payload.recognitionData;
    },
    setInsuranceAiScanDocument(state, action: PayloadAction<{ insuranceScan: MediaType | null }>) {
      state.insuranceScanAiDocument = action.payload.insuranceScan;
    },
    clearInsuranceRecognitionData(state) {
      state.insuranceRecognitionData = initialState.insuranceRecognitionData;
    },
    // ---------- vehicle registration recognition data --------------

    setVehicleRegistrationRecognitionData(
      state,
      action: PayloadAction<{
        recognitionData: Nullable<VehicleRegistrationRecognitionData>;
        vehicleRegistrationScanDocument: Nullable<MediaType>;
      }>,
    ) {
      state.vehicleRegistrationScanData.vehicleRegistrationRecognitionData = action.payload.recognitionData;
      state.vehicleRegistrationScanData.vehicleRegistrationScanDocument =
        action.payload.vehicleRegistrationScanDocument;
    },
    resetVehicleRegistrationRecognitionData(state) {
      state.vehicleRegistrationScanData.vehicleRegistrationRecognitionData = null;
      state.vehicleRegistrationScanData.vehicleRegistrationScanDocument = null;
    },

    // ----------------  garage share -------------------------
    setGarageAssignPeopleLists(
      state,
      action: PayloadAction<{ peopleLists: AssignPeopleSelectValueModel[] }>,
    ) {
      state.share.sharePeopleList = action.payload.peopleLists;
      state.currentTransport.users = action.payload.peopleLists;
    },
    setGarageSelectedUser(state, action: PayloadAction<{ selectUser: AssignPeopleSelectValueModel | null }>) {
      state.share.selectShareUser = action.payload.selectUser;
    },
    addGarageShareUser(state, action: PayloadAction<{ shareUser: AssignPeopleSelectValueModel }>) {
      state.currentTransport.users.push(action.payload.shareUser);
    },
    deleteGarageShareUser(state, action: PayloadAction<{ shareUserID: number }>) {
      const index = state.currentTransport.users.findIndex(
        (user: any) => user.id === action.payload.shareUserID,
      );
      if (index !== -1) state.currentTransport.users.splice(index, 1);
    },
    updateGarageShareUser(
      state,
      action: PayloadAction<{ userRole: AssignPeoplePermissionsEnum; shareUserID: number }>,
    ) {
      const index = state.currentTransport.users.findIndex(
        (user: any) => user.id === action.payload.shareUserID,
      );
      if (index !== -1) state.currentTransport.users[index].role = action.payload.userRole;
    },

    // ----------------- web-sockets -------------------
    updateCoverPhotoWS(state, action: PayloadAction<{ newCoverPhoto: MediaType; transportID: number }>) {
      const index = state.transports.data.findIndex(
        (transport) => transport.id === action.payload.transportID,
      );

      if (index !== -1) state.transports.data[index].cover_photo = action.payload.newCoverPhoto;
    },
  },
  extraReducers: ({ addCase }) => {
    addCase(getTransportsV2.fulfilled, (state, { payload }) => {
      if (payload.meta.current_page === 1) {
        state.transports.data = payload.data;
        state.transports.meta = payload.meta;
        state.isListOver = isListOver(payload);
      } else {
        state.transports.data.push(...payload.data);
        state.transports.meta = payload.meta;
        state.isListOver = isListOver(payload);
      }
    });

    // ----------------- web-sockets -------------------
    addCase(
      updateTransportWS.fulfilled,
      (state, action: PayloadAction<{ updateTransportWS: RootGarageItemsData }>) => {
        if (!!state.transports?.data?.length) {
          const index = state.transports.data.findIndex(
            (transport) => transport.id === action.payload?.updateTransportWS?.id,
          );

          if (index !== -1) state.transports.data[index] = action.payload.updateTransportWS;
        }
      },
    );
    addCase(
      acceptedTransportWS.fulfilled,
      (state, action: PayloadAction<{ acceptedTransportWS: RootGarageItemsData }>) => {
        const index = state.transports.data.findIndex(
          (transport) => transport.id === action.payload.acceptedTransportWS.id,
        );
        state.transports.data.splice(index, 1);

        const pending = state.transports.data.filter(
          (transport) => transport.current.confirm_status === StatusTransport.pending,
        );
        const general = state.transports.data.filter(
          (transport) => transport.current.confirm_status !== StatusTransport.pending,
        );

        state.transports.data = [...pending, action.payload.acceptedTransportWS, ...general];
      },
    );
    addCase(
      addTransportWS.fulfilled,
      (state, action: PayloadAction<{ addTransportWS: RootGarageItemsData }>) => {
        const index = state.transports.data.findIndex(
          (transport) => transport.id === action.payload.addTransportWS.id,
        );

        if (index !== -1) {
          state.transports.data[index] = action.payload.addTransportWS;
        } else {
          state.transports.data = !!state.transports.data.length
            ? [action.payload.addTransportWS, ...state.transports.data]
            : [action.payload.addTransportWS];
        }
      },
    );
  },
});

export const {
  clearTransportsState,
  setFilterParams,
  setLoadingGarage,
  isEmptyDataGarageWithFilter,
  deleteCurrentTransport,
  setTransportDataWithNewPosition,
  setCurrentTransport,
  deleteCurrentInsurance,
  updateCoverPhoto,
  removeCoverPhoto,
  addPhoto,
  addDocuments,
  setEditMode,
  updateCoverPhotoWS,
  setInsuranceAiScanDocument,
  setInsuranceRecognitionData,
  clearInsuranceRecognitionData,
  deleteCurrentLicence,
  deleteCurrentSticker,
  setGarageAssignPeopleLists,
  clearGarageFilters,
  setGarageSelectedUser,
  addGarageShareUser,
  deleteGarageShareUser,
  updateGarageShareUser,
  setVehicleRegistrationRecognitionData,
  resetVehicleRegistrationRecognitionData,
} = garageSlice.actions;
export const garageReducer = garageSlice.reducer;
